import React, { useState, useEffect, useRef } from 'react'
import { X, Plus, Trash2, RotateCcw, Search } from 'lucide-react'
import type { Order, OrderCreateData, OrderUpdateData, AdditionalStop, PricingDetail } from '@/api/orders'
import { createOrder, updateOrder } from '@/api/orders'
import { lookupFlight } from '@/api/flights'
import type { FlightInfo } from '@/api/flights'
import { Input } from "@/components/ui/input"
import { InputWithClear } from "@/components/ui/input-with-clear"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { MonthDayPicker } from "@/components/ui/month-day-picker"
import { TimePicker } from "@/components/ui/time-picker"
import { Counter } from "@/components/ui/counter"
import { cn } from "@/lib/utils"
import type { Driver, Tenant, Customer } from '@/types'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'
import PassengerAutocomplete from '@/components/orders/PassengerAutocomplete'
import CustomerCombobox from '@/components/ui/CustomerCombobox'
import DriverCombobox from '@/components/ui/DriverCombobox'
import { leaveApi } from '@/api/leaves'

interface OrderDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: OrderCreateData | OrderUpdateData) => Promise<Order | void>
  onSaveAsNew?: (data: OrderCreateData) => Promise<void>
  onSaved?: (items: { order: Order; label: string }[]) => void
  order?: Order | null
  drivers: Driver[]
  customers?: Customer[]
  onRequestAddCustomer?: () => void
  autoSelectCustomerId?: string
  previewImageUrl?: string
  aiRawText?: string
  onCustomSignFileChange?: (file: File | null) => void
}

interface ReturnTripData {
  order_category: string
  pickup_date: string
  pickup_year?: number
  pickup_time: string
  pickup_location: string
  dropoff_location: string
  flight_number?: string
  departure_time?: string
  arrival_time?: string
  arrival_terminal?: string
  name_board: boolean
  pricing: PricingDetail
}

interface OrderFormData {
  order_category: string
  status: string
  customer_id?: string
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  pickup_date: string
  pickup_year?: number
  pickup_time: string
  pickup_location: string
  dropoff_location: string
  is_round_trip: boolean
  flight_number?: string
  departure_time?: string
  arrival_time?: string
  arrival_terminal?: string
  vehicle_type: string
  luggage_count: number
  child_seat: number
  booster_seat: number
  name_board: boolean
  is_signed: boolean
  pricing: PricingDetail
  payment_method: string
  driver_notes?: string
  wait_minutes?: string
  extra_amount?: string
  driver_id: string
  driver_name: string
  driver_phone: string
  vehicle_number: string
  additional_stops?: AdditionalStop[]
  return_trip?: ReturnTripData
  vendor_name?: string
  vendor_paid_at?: string
  vendor2_name?: string
  vendor2_paid_at?: string
  dispatch_template_key?: string
}

const AIRPORT_BASES = ["桃園機場", "松山機場", "台中機場", "高雄機場", "基隆港"]
const AIRPORT_NORMALIZE: Record<string, string> = {
  "台中清泉崗機場": "台中機場",
  "高雄小港機場": "高雄機場",
}
const TAOYUAN_TERMINALS = ["第1航廈", "第2航廈"]

const DEFAULT_VEHICLE_OPTIONS = ["小車", "休旅車", "大車"]
const DEFAULT_PAYMENT_OPTIONS = ["現金", "刷卡", "轉帳", "月結", "匯款"]
const STATUS_OPTIONS = ["待派車", "已派車", "進行中", "已完成", "已取消"]

const getReversedCategory = (cat: string) => {
  if (cat === '送機') return '接機'
  if (cat === '接機') return '送機'
  return '其他'
}

const getEmptyPricing = (isTaxInclusiveDefault = true): PricingDetail => ({
  total_amount: 0, driver_amount: 0, base_fare: 0,
  night_surcharge: 0, cross_district_fee: 0,
  driver_fee: 0, vendor_rebate: 0, vendor_rebate_2: 0, company_rebate: 0,
  is_tax_inclusive: isTaxInclusiveDefault,
  name_board_fee: 0, child_seat_fee: 0, booster_seat_fee: 0,
})

const getTenantAddonPrices = () => {
  const tenantStr = localStorage.getItem('tenant')
  const cfg = tenantStr ? JSON.parse(tenantStr).form_config : null
  return {
    name_board_price: cfg?.name_board_price ?? 0,
    child_seat_price: cfg?.child_seat_price ?? 0,
    booster_seat_price: cfg?.booster_seat_price ?? 0,
  }
}

/** 車行是否開啟「依比例自動填入司機費用/廠商回金」，及回金比例（預設關閉，比例預設 10%） */
const getFeeSplitConfig = () => {
  const tenantStr = localStorage.getItem('tenant')
  const cfg = tenantStr ? JSON.parse(tenantStr).form_config : null
  return {
    enabled: cfg?.enable_fee_split ?? false,
    vendorPercent: cfg?.fee_split_vendor_percent ?? 10,
  }
}

/** 租戶除了預設派車單樣板外，還有沒有設定其他具名樣板可選（設定 → 文檔樣板配置 → dispatch_templates）
 *  "xunan"（迅安簽單）有自己專屬的勾選框（見下方 isXunanSignEnabled），這裡排除掉避免重複出現 */
const getDispatchTemplateOptions = (): { key: string; name: string }[] => {
  const tenantStr = localStorage.getItem('tenant')
  const templates = tenantStr ? JSON.parse(tenantStr).document_templates?.dispatch_templates : null
  if (!templates || typeof templates !== 'object') return []
  return Object.entries(templates)
    .filter(([key]) => key !== 'xunan')
    .map(([key, cfg]: [string, any]) => ({ key, name: cfg?.name || key }))
}

/** 車行是否在設定裡開啟「迅安簽單」（預設關閉，其他車行不會看到這個勾選） */
const isXunanSignEnabled = (): boolean => {
  const tenantStr = localStorage.getItem('tenant')
  return tenantStr ? !!JSON.parse(tenantStr).form_config?.enable_xunan_sign : false
}

/** 車行是否在設定裡開啟「單程/包車」類別（預設關閉，維持原本 接機/送機/其他） */
const isExtendedCategoriesEnabled = (): boolean => {
  const tenantStr = localStorage.getItem('tenant')
  return tenantStr ? !!JSON.parse(tenantStr).form_config?.enable_extended_categories : false
}

/** 車行是否在設定裡開啟「第二組廠商回金」（預設關閉） */
const isSecondVendorRebateEnabled = (): boolean => {
  try {
    const tenantStr = localStorage.getItem('tenant')
    return tenantStr ? !!JSON.parse(tenantStr).form_config?.enable_second_vendor_rebate : false
  } catch { return false }
}

/** 車行是否在設定裡開啟「等待時間／追加金額」（預設關閉） */
const isWaitExtraEnabled = (): boolean => {
  const tenantStr = localStorage.getItem('tenant')
  return tenantStr ? !!JSON.parse(tenantStr).form_config?.enable_wait_extra : false
}

const ORDER_CATEGORY_OPTIONS = ['送機', '接機', '其他']
const ORDER_CATEGORY_OPTIONS_EXTENDED = ['送機', '接機', '單程', '包車']

/** 依照舉牌是否勾選，在備註文字裡加上/移除「舉牌」——來回程各自獨立判斷，
 *  避免去程備註裡的「舉牌」文字被原封不動複製到沒勾舉牌的回程單 */
const applyNameBoardNote = (notes: string | undefined, nameBoardOn: boolean): string | undefined => {
  const base = notes || ''
  if (nameBoardOn) {
    return base.includes('舉牌') ? base : (base ? `${base} 舉牌` : '舉牌')
  }
  const stripped = base.replace(/\s*舉牌\s*/g, ' ').trim()
  return stripped || undefined
}

const getTodayDate = () => {
  const today = new Date()
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`
}

const formatToISO8601 = (dateStr: string, timeStr: string, year?: number) => {
  const currentYear = year ?? new Date().getFullYear()
  const [month, day] = dateStr.split('-')
  const [hours, minutes] = timeStr.split(':')
  return `${currentYear}-${month}-${day}T${hours}:${minutes}:00`
}

const parseISOToMonthDay = (isoString: string) => {
  const d = new Date(isoString)
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
}

const parseISOToTime = (isoString: string) => {
  const d = new Date(isoString)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

const normalizeAirportLocation = (loc: string): string => {
  if (loc === '桃園機場一航廈') return '桃園機場第1航廈'
  if (loc === '桃園機場二航廈') return '桃園機場第2航廈'
  if (loc === '桃園機場三航廈') return '桃園機場第3航廈'
  return loc
}

const getInitialFormData = (): OrderFormData => {
  // 從 localStorage 讀取租戶預設設定
  const tenantStr = localStorage.getItem('tenant')
  const isTaxInclusiveDefault = tenantStr ? (JSON.parse(tenantStr).form_config?.pricing_is_tax_inclusive_default ?? true) : true

  return {
    order_category: '送機', status: '待派車', customer_id: undefined,
    passenger_name: '貴賓', passenger_phone: '', passenger_count: 1,
    pickup_date: getTodayDate(), pickup_year: new Date().getFullYear(), pickup_time: '00:00',
    pickup_location: '', dropoff_location: '',
    is_round_trip: false, flight_number: '', departure_time: '', arrival_time: '', arrival_terminal: '', vehicle_type: '',
    luggage_count: 0, child_seat: 0, booster_seat: 0,
    name_board: false, is_signed: false,
    pricing: getEmptyPricing(isTaxInclusiveDefault),
    payment_method: '現金',
    driver_id: '', driver_name: '', driver_phone: '', vehicle_number: '',
    vendor_name: '', vendor_paid_at: '',
    vendor2_name: '', vendor2_paid_at: '',
    dispatch_template_key: '',
  }
}

const buildFormFromOrder = (order: any, isTaxInclusiveDefault?: boolean): OrderFormData => {
  if (isTaxInclusiveDefault === undefined) {
    const tenantStr = localStorage.getItem('tenant')
    isTaxInclusiveDefault = tenantStr ? (JSON.parse(tenantStr).form_config?.pricing_is_tax_inclusive_default ?? true) : true
  }
  let pickupDate = getTodayDate(), pickupTime = '00:00'
  let pickupYear = new Date().getFullYear()
  if (order.pickup_datetime) {
    pickupYear = new Date(order.pickup_datetime).getFullYear()
    pickupDate = parseISOToMonthDay(order.pickup_datetime)
    pickupTime = parseISOToTime(order.pickup_datetime)
  }
  const category = order.order_category || '送機'

  // 優先使用新的 airport/terminal 字段，否則使用 location 字符串
  let pickupLoc = order.pickup_location || ''
  if (order.pickup_airport && order.pickup_terminal) {
    pickupLoc = `${order.pickup_airport}${order.pickup_terminal}`
  } else {
    pickupLoc = normalizeAirportLocation(pickupLoc)
  }

  let dropoffLoc = order.dropoff_location || ''
  if (order.dropoff_airport && order.dropoff_terminal) {
    dropoffLoc = `${order.dropoff_airport}${order.dropoff_terminal}`
  } else {
    dropoffLoc = normalizeAirportLocation(dropoffLoc)
  }

  return {
    order_category: category,
    status: order.status || '待派車',
    customer_id: order.customer_id || undefined,
    passenger_name: order.passenger_name || '貴賓',
    passenger_phone: order.passenger_phone || '',
    passenger_count: order.passenger_count || 1,
    pickup_date: pickupDate, pickup_year: pickupYear, pickup_time: pickupTime,
    pickup_location: category === '接機' ? (pickupLoc || '桃園機場') : pickupLoc,
    dropoff_location: category === '送機' ? (dropoffLoc || '桃園機場') : dropoffLoc,
    is_round_trip: order.is_round_trip || false,
    flight_number: order.flight_number || '',
    departure_time: order.departure_time || '',
    arrival_time: order.arrival_time || '',
    arrival_terminal: order.arrival_terminal || '',
    vehicle_type: order.vehicle_type || '',
    luggage_count: order.luggage_count || 0,
    child_seat: order.child_seat || 0,
    booster_seat: order.booster_seat || 0,
    name_board: order.name_board || false,
    is_signed: order.is_signed || false,
    pricing: {
      // 先帶入原訂單所有費用欄位，避免舉牌費／兒童座椅費等沒列在下面的欄位在儲存時被歸 0
      ...(order.pricing || {}),
      total_amount: order.pricing?.total_amount || 0,
      driver_amount: order.pricing?.driver_amount || 0,
      base_fare: order.pricing?.base_fare || order.pricing?.total_amount || 0,
      night_surcharge: order.pricing?.night_surcharge || 0,
      cross_district_fee: order.pricing?.cross_district_fee || 0,
      driver_fee: order.pricing?.driver_fee || 0,
      vendor_rebate: order.pricing?.vendor_rebate || 0,
      vendor_rebate_2: order.pricing?.vendor_rebate_2 || 0,
      company_rebate: order.pricing?.company_rebate || 0,
      name_board_fee: order.pricing?.name_board_fee || 0,
      child_seat_fee: order.pricing?.child_seat_fee || 0,
      booster_seat_fee: order.pricing?.booster_seat_fee || 0,
      extra_passenger_fee: order.pricing?.extra_passenger_fee || 0,
      is_tax_inclusive: order.pricing?.is_tax_inclusive ?? isTaxInclusiveDefault,
    },
    payment_method: order.payment_method || '現金',
    driver_notes: order.driver_notes || '',
    wait_minutes: order.wait_minutes != null ? String(order.wait_minutes) : '',
    extra_amount: order.extra_amount != null ? String(order.extra_amount) : '',
    driver_id: order.driver_id || '',
    driver_name: order.driver_name || '',
    driver_phone: order.driver_phone || '',
    vehicle_number: order.vehicle_number || '',
    additional_stops: order.additional_stops || [],
    vendor_name: order.vendor_name || '',
    vendor_paid_at: order.vendor_paid_at ? order.vendor_paid_at.substring(0, 10) : '',
    vendor2_name: order.vendor2_name || '',
    vendor2_paid_at: order.vendor2_paid_at ? order.vendor2_paid_at.substring(0, 10) : '',
    dispatch_template_key: order.dispatch_template_key || '',
  }
}

const calcTotal = (p: PricingDetail, nameboard: boolean) =>
  (p.base_fare || 0) + (p.night_surcharge || 0) + (p.cross_district_fee || 0) +
  (nameboard ? (p.name_board_fee || 0) : 0) + (p.child_seat_fee || 0) + (p.booster_seat_fee || 0)

function ImagePreviewPanel({ url }: { url: string }) {
  const [collapsed, setCollapsed] = React.useState(false)
  return (
    <div className="lg:w-[450px] lg:shrink-0 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col lg:overflow-hidden">
      {/* 標題列：手機可收折 */}
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        className="flex items-center justify-between px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 shrink-0 lg:cursor-default"
      >
        <span>原始圖片</span>
        <span className="lg:hidden text-gray-400">{collapsed ? '▼ 展開' : '▲ 收折'}</span>
      </button>
      {/* 圖片 */}
      {!collapsed && (
        <div className="overflow-auto p-2 flex items-start justify-center lg:flex-1">
          <img
            src={url}
            alt="辨識來源圖片"
            className="max-w-full rounded-md object-contain shadow-sm"
            style={{ maxHeight: '40vh' }}
          />
        </div>
      )}
    </div>
  )
}

/** AI 解析原文對照：桌機放在 drawer 左側固定欄，方便跟右側表單對照；手機收合在上方，跟 ImagePreviewPanel 同一套模式 */
function AiRawTextPanel({ text }: { text: string }) {
  const [collapsed, setCollapsed] = React.useState(false)
  return (
    <div className="lg:w-[380px] lg:shrink-0 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-700 bg-blue-50/50 dark:bg-blue-900/10 flex flex-col lg:overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        className="flex items-center justify-between px-3 py-2 text-xs font-bold text-blue-500 dark:text-blue-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 shrink-0 lg:cursor-default"
      >
        <span>AI 解析原文</span>
        <span className="lg:hidden text-gray-400">{collapsed ? '▼ 展開' : '▲ 收折'}</span>
      </button>
      {!collapsed && (
        <div className="overflow-auto p-3 lg:flex-1">
          <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  )
}

export default function OrderDrawer({ isOpen, onClose, onSubmit, onSaveAsNew, onSaved, order, drivers, customers = [], onRequestAddCustomer, autoSelectCustomerId, previewImageUrl, aiRawText, onCustomSignFileChange }: OrderDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [returnTripError, setReturnTripError] = useState<string | null>(null)
  const [newStopInput, setNewStopInput] = useState('')
  const [newStopName, setNewStopName] = useState('')
  const [newStopPhone, setNewStopPhone] = useState('')
  const [newStopError, setNewStopError] = useState('')
  const timePickerRef = useRef<{ focus: () => void }>(null)
  const [flightInfo, setFlightInfo] = useState<FlightInfo | null>(null)
  const [flightMsg, setFlightMsg] = useState<string>('')
  const [flightLoading, setFlightLoading] = useState(false)
  const [returnFlightInfo, setReturnFlightInfo] = useState<FlightInfo | null>(null)
  const [returnFlightMsg, setReturnFlightMsg] = useState<string>('')
  const [returnFlightLoading, setReturnFlightLoading] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState<OrderFormData>(() => order ? buildFormFromOrder(order) : getInitialFormData())
  // 金額快速加價共用一排按鈕，作用在「最後點選/聚焦」的金額欄位上（主單/回程各自記自己的）
  const [activeFeeField, setActiveFeeField] = useState<{ section: 'main' | 'return'; field: keyof PricingDetail; label: string }>(
    { section: 'main', field: 'base_fare', label: '車資' }
  )
  const [vehicleOptions, setVehicleOptions] = useState<string[]>(DEFAULT_VEHICLE_OPTIONS)
  const [paymentOptions, setPaymentOptions] = useState<string[]>(DEFAULT_PAYMENT_OPTIONS)
  const [dispatchTemplateOptions] = useState(() => getDispatchTemplateOptions())
  const [xunanSignEnabled] = useState(() => isXunanSignEnabled())
  const [waitExtraEnabled] = useState(() => isWaitExtraEnabled())
  const [secondVendorEnabled] = useState(() => isSecondVendorRebateEnabled())
  const [categoryOptions] = useState(() => isExtendedCategoriesEnabled() ? ORDER_CATEGORY_OPTIONS_EXTENDED : ORDER_CATEGORY_OPTIONS)
  const [vendorOptions, setVendorOptions] = useState<string[]>([])
  const [showVendors, setShowVendors] = useState(false)
  const [hiddenFields, setHiddenFields] = useState<string[]>([])
  const [onLeaveDriverIds, setOnLeaveDriverIds] = useState<string[]>([])
  const [customSignEnabled, setCustomSignEnabled] = useState(false)
  const [customSignFile, setCustomSignFile] = useState<File | null>(null)
  const [customSignPreview, setCustomSignPreview] = useState<string | null>(null)

  // helper: 是否隱藏某欄位
  const isHidden = (field: string) => hiddenFields.includes(field)

  useEffect(() => {
    const tenantStr = localStorage.getItem('tenant')
    if (tenantStr) {
      try {
        const t = JSON.parse(tenantStr) as Tenant
        if (t.vehicle_categories?.length) {
          setVehicleOptions(t.vehicle_categories.map((c: any) => typeof c === 'string' ? c : c.name || '').filter(Boolean))
        }
        if (t.payment_methods?.length) {
          setPaymentOptions(t.payment_methods.map((m: any) => typeof m === 'string' ? m : m.name || '').filter(Boolean))
        }
        if (t.form_config?.order?.hidden_fields?.length) {
          setHiddenFields(t.form_config.order.hidden_fields)
        }
        setShowVendors(t.form_config?.show_vendors ?? false)
        if (t.form_config?.vendors?.length) {
          setVendorOptions(t.form_config.vendors.map((v: any) => typeof v === 'string' ? v : v.name || '').filter(Boolean))
        }
      } catch {}
    }
  }, [])

  useEffect(() => {
    if (order) {
      setFormData(buildFormFromOrder(order))
    } else {
      setFormData(getInitialFormData())
    }
    setReturnTripError(null)
    // 「新增第三方上車點」輸入框是獨立狀態，換一張訂單/重新開啟都要清空，不然會帶到上一張的資料，
    // 存檔時還會被當成這張訂單的中途點自動加進去
    setNewStopInput('')
    setNewStopName('')
    setNewStopPhone('')
    setNewStopError('')
    setFlightInfo(null)
    setFlightMsg('')
    setCustomSignFile(null)
    onCustomSignFileChange?.(null)
    // 編輯既有訂單時，恢復客製簽單狀態
    if (order?.custom_sign_image_path) {
      setCustomSignEnabled(true)
      const filename = order.custom_sign_image_path.split(/[/\\]/).pop()
      setCustomSignPreview(`/uploads/custom_signs/${filename}`)
    } else {
      setCustomSignEnabled(false)
      setCustomSignPreview(null)
    }
  }, [order, isOpen])

  useEffect(() => {
    if (autoSelectCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === autoSelectCustomerId)
      if (customer) {
        setFormData(prev => ({ ...prev, customer_id: autoSelectCustomerId }))
      }
    }
  }, [autoSelectCustomerId, customers])

  useEffect(() => {
    if (!isOpen || !formData.pickup_date) {
      setOnLeaveDriverIds([])
      return
    }
    const fullDate = `${formData.pickup_year ?? new Date().getFullYear()}-${formData.pickup_date}`
    let cancelled = false
    leaveApi.getOnDateDriverIds(fullDate)
      .then(ids => { if (!cancelled) setOnLeaveDriverIds(ids) })
      .catch(() => { if (!cancelled) setOnLeaveDriverIds([]) })
    return () => { cancelled = true }
  }, [formData.pickup_date, formData.pickup_year, isOpen])

  const handleFlightLookup = async () => {
    const flight = (formData.flight_number || '').trim()
    const date = formData.pickup_date
    if (!flight || !date) return
    // 將 MM-DD 轉換為 YYYY-MM-DD
    const year = formData.pickup_year ?? new Date().getFullYear()
    const fullDate = `${year}-${date}`
    setFlightLoading(true)
    setFlightInfo(null)
    setFlightMsg('')
    try {
      const result = await lookupFlight(flight, fullDate)
      if (result.found) {
        setFlightInfo(result)
        setFlightMsg('')
        setFormData(prev => ({
          ...prev,
          departure_time: result.departure_time || '',
          arrival_time: result.arrival_time || '',
          arrival_terminal: result.arrival_terminal || '',
        }))
      } else {
        setFlightMsg(result.message)
      }
    } catch {
      setFlightMsg('查詢失敗，請稍後再試')
    } finally {
      setFlightLoading(false)
    }
  }

  const handleReturnFlightLookup = async () => {
    const flight = (formData.return_trip?.flight_number || '').trim()
    const date = formData.return_trip?.pickup_date
    if (!flight || !date) return
    const year = formData.return_trip?.pickup_year ?? new Date().getFullYear()
    const fullDate = `${year}-${date}`
    setReturnFlightLoading(true)
    setReturnFlightInfo(null)
    setReturnFlightMsg('')
    try {
      const result = await lookupFlight(flight, fullDate)
      if (result.found) {
        setReturnFlightInfo(result)
        setReturnFlightMsg('')
        setReturnTrip({
          departure_time: result.departure_time || '',
          arrival_time: result.arrival_time || '',
          arrival_terminal: result.arrival_terminal || '',
        })
      } else {
        setReturnFlightMsg(result.message)
      }
    } catch {
      setReturnFlightMsg('查詢失敗，請稍後再試')
    } finally {
      setReturnFlightLoading(false)
    }
  }

  // 勾選來回時自動填入回程預設值
  const handleRoundTripToggle = (checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        is_round_trip: true,
        return_trip: {
          order_category: getReversedCategory(prev.order_category),
          pickup_date: prev.pickup_date,
          pickup_year: prev.pickup_year,
          pickup_time: prev.pickup_time,
          pickup_location: prev.dropoff_location,
          dropoff_location: prev.pickup_location,
          flight_number: '',
          name_board: false,
          pricing: getEmptyPricing(),
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, is_round_trip: false, return_trip: undefined }))
    }
  }

  const setReturnTrip = (patch: Partial<ReturnTripData>) => {
    setFormData(prev => ({
      ...prev,
      return_trip: { ...prev.return_trip!, ...patch }
    }))
  }

  const setReturnPricing = (patch: Partial<PricingDetail>) => {
    setFormData(prev => ({
      ...prev,
      return_trip: {
        ...prev.return_trip!,
        pricing: { ...prev.return_trip!.pricing, ...patch }
      }
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!formData.passenger_name?.trim()) errors.passenger_name = '請填寫貴賓姓名'
    if (!formData.pickup_location?.trim()) errors.pickup_location = '請填寫上車地點'
    if (!formData.dropoff_location?.trim()) errors.dropoff_location = '請填寫下車地點'
    if (!formData.pickup_date) errors.pickup_date = '請選擇日期'
    if (!formData.pickup_time) errors.pickup_time = '請選擇時間'
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    setFormErrors({})
    setIsSubmitting(true)
    setReturnTripError(null)
    try {
      // 還在輸入框、尚未按「新增」的最後一個點也要算進去，去程與回程都用同一份，避免回程少一個點
      const finalStops = getStopsWithPendingInput()
      const submitData: OrderCreateData | OrderUpdateData = {
        order_category: formData.order_category,
        customer_id: formData.customer_id || null,
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        passenger_count: formData.passenger_count,
        pickup_datetime: formatToISO8601(formData.pickup_date, formData.pickup_time, formData.pickup_year),
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        is_round_trip: formData.is_round_trip,
        flight_number: formData.flight_number,
        departure_time: formData.departure_time,
        arrival_time: formData.arrival_time,
        arrival_terminal: formData.arrival_terminal,
        vehicle_type: formData.vehicle_type,
        luggage_count: formData.luggage_count,
        child_seat: formData.child_seat,
        booster_seat: formData.booster_seat,
        name_board: formData.name_board,
        is_signed: formData.is_signed,
        pricing: {
          ...formData.pricing!,
          total_amount: calcTotal(formData.pricing, formData.name_board),
        },
        payment_method: formData.payment_method,
        driver_notes: formData.driver_notes,
        ...(waitExtraEnabled ? {
          wait_minutes: formData.wait_minutes ? parseInt(formData.wait_minutes, 10) || 0 : null,
          extra_amount: formData.extra_amount ? parseInt(formData.extra_amount, 10) || 0 : null,
        } : {}),
        additional_stops: finalStops,
        status: formData.status,
        driver_id: formData.driver_id || undefined,
        driver_name: formData.driver_name || undefined,
        driver_phone: formData.driver_phone || undefined,
        vehicle_number: formData.vehicle_number || undefined,
        vendor_name: formData.vendor_name || undefined,
        vendor_paid_at: formData.vendor_paid_at ? formData.vendor_paid_at + 'T00:00:00' : undefined,
        vendor2_name: formData.vendor2_name || undefined,
        vendor2_paid_at: formData.vendor2_paid_at ? formData.vendor2_paid_at + 'T00:00:00' : undefined,
        dispatch_template_key: formData.dispatch_template_key || undefined,
      }
      const mainOrder = (await onSubmit(submitData)) as Order | undefined
      const savedItems: { order: Order; label: string }[] = []

      // 建立回程訂單
      if (formData.is_round_trip && formData.return_trip) {
        const rt = formData.return_trip
        try {
          const returnOrder = await createOrder({
            order_category: rt.order_category,
            customer_id: formData.customer_id || null,
            passenger_name: formData.passenger_name,
            passenger_phone: formData.passenger_phone,
            passenger_count: formData.passenger_count,
            pickup_datetime: formatToISO8601(rt.pickup_date, rt.pickup_time, rt.pickup_year),
            pickup_location: rt.pickup_location,
            dropoff_location: rt.dropoff_location,
            is_round_trip: false,
            flight_number: rt.flight_number,
            departure_time: rt.departure_time,
            arrival_time: rt.arrival_time,
            arrival_terminal: rt.arrival_terminal,
            vehicle_type: formData.vehicle_type,
            luggage_count: formData.luggage_count,
            child_seat: formData.child_seat,
            booster_seat: formData.booster_seat,
            name_board: rt.name_board,
            is_signed: formData.is_signed,
            pricing: {
              ...rt.pricing,
              total_amount: calcTotal(rt.pricing, rt.name_board),
            },
            payment_method: formData.payment_method,
            // 備註文字要跟回程自己的舉牌勾選一致，不能直接沿用去程的備註——
            // 去程有勾舉牌時備註會被加上「舉牌」文字，若照抄會讓沒勾舉牌的回程單也顯示舉牌
            driver_notes: applyNameBoardNote(formData.driver_notes, rt.name_board),
            ...(waitExtraEnabled ? {
              wait_minutes: formData.wait_minutes ? parseInt(formData.wait_minutes, 10) || 0 : null,
              extra_amount: formData.extra_amount ? parseInt(formData.extra_amount, 10) || 0 : null,
            } : {}),
            additional_stops: finalStops,
            status: '待派車',
          } as any)

          // 互相關聯去程/回程訂單，方便之後在列表上互相開啟
          if (mainOrder?.id && returnOrder?.id) {
            try {
              await Promise.all([
                updateOrder(mainOrder.id, { linked_order_id: returnOrder.id }),
                updateOrder(returnOrder.id, { linked_order_id: mainOrder.id }),
              ])
            } catch (e) {
              console.error('關聯來回程訂單失敗', e)
            }
            savedItems.push({ order: mainOrder, label: '去程' })
            savedItems.push({ order: returnOrder, label: '回程' })
          }
        } catch {
          setReturnTripError('主單已建立，但回程訂單建立失敗，請手動新增。')
          return
        }
      } else if (mainOrder?.id) {
        savedItems.push({ order: mainOrder, label: '' })
      }

      if (savedItems.length > 0) {
        onSaved?.(savedItems)
      }

      onClose()
    } catch (error) {
      console.error('Failed to submit order:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveAsNew = async () => {
    if (!onSaveAsNew) return
    const errors: Record<string, string> = {}
    if (!formData.passenger_name?.trim()) errors.passenger_name = '請填寫貴賓姓名'
    if (!formData.pickup_location?.trim()) errors.pickup_location = '請填寫上車地點'
    if (!formData.dropoff_location?.trim()) errors.dropoff_location = '請填寫下車地點'
    if (!formData.pickup_date) errors.pickup_date = '請選擇日期'
    if (!formData.pickup_time) errors.pickup_time = '請選擇時間'
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    setFormErrors({})
    setIsSubmitting(true)
    try {
      const finalStops = getStopsWithPendingInput()
      const newOrderData: OrderCreateData = {
        order_category: formData.order_category,
        customer_id: formData.customer_id || null,
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        passenger_count: formData.passenger_count,
        pickup_datetime: formatToISO8601(formData.pickup_date, formData.pickup_time, formData.pickup_year),
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        is_round_trip: formData.is_round_trip,
        flight_number: formData.flight_number,
        departure_time: formData.departure_time,
        arrival_time: formData.arrival_time,
        arrival_terminal: formData.arrival_terminal,
        vehicle_type: formData.vehicle_type,
        luggage_count: formData.luggage_count,
        child_seat: formData.child_seat,
        booster_seat: formData.booster_seat,
        name_board: formData.name_board,
        is_signed: false,
        pricing: {
          ...formData.pricing!,
          total_amount: calcTotal(formData.pricing, formData.name_board),
        },
        payment_method: formData.payment_method,
        driver_notes: formData.driver_notes,
        ...(waitExtraEnabled ? {
          wait_minutes: formData.wait_minutes ? parseInt(formData.wait_minutes, 10) || 0 : null,
          extra_amount: formData.extra_amount ? parseInt(formData.extra_amount, 10) || 0 : null,
        } : {}),
        additional_stops: finalStops,
        status: '待派車',
        // 司機、車號、簽名/簽收一律清空為新單預設值，不沿用原單
        driver_id: undefined,
        driver_name: undefined,
        driver_phone: undefined,
        vehicle_number: undefined,
        vendor_name: undefined,
        vendor_paid_at: undefined,
        vendor2_name: undefined,
        vendor2_paid_at: undefined,
      }
      await onSaveAsNew(newOrderData)
      onClose()
    } catch (error) {
      console.error('Failed to save as new order:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // 已新增的中途點 + 輸入框裡還沒按「新增」的那一點
  const getStopsWithPendingInput = () =>
    newStopInput.trim()
      ? [...(formData.additional_stops || []), {
          address: newStopInput.trim(),
          order: (formData.additional_stops?.length || 0) + 1,
          name: newStopName.trim() || undefined,
          phone: newStopPhone.trim() || undefined,
        }]
      : formData.additional_stops

  const handleDriverChange = (driverId: string) => {
    const driver = drivers.find(d => d.id === driverId)
    setFormData({
      ...formData,
      driver_id: driverId || '',
      driver_name: driver?.name || '',
      driver_phone: driver?.phone || '',
      vehicle_number: driver?.vehicle_plate || '',
      status: driverId && formData.status === '待派車' ? '已派車' : formData.status
    })
  }

  const handleAddStop = () => {
    if (!newStopInput.trim()) {
      setNewStopError('請填寫地址')
      return
    }
    const currentStops = formData.additional_stops || []
    setFormData({
      ...formData,
      additional_stops: [...currentStops, {
        address: newStopInput.trim(),
        order: currentStops.length + 1,
        name: newStopName.trim() || undefined,
        phone: newStopPhone.trim() || undefined,
      }],
    })
    setNewStopInput('')
    setNewStopName('')
    setNewStopPhone('')
    setNewStopError('')
  }

  const handleRemoveStop = (index: number) => {
    setFormData({ ...formData, additional_stops: (formData.additional_stops || []).filter((_, i) => i !== index) })
  }

  const handleEditStop = (index: number, field: 'address' | 'name' | 'phone', newValue: string) => {
    const newStops = [...(formData.additional_stops || [])]
    newStops[index] = { ...newStops[index], [field]: newValue }
    setFormData({ ...formData, additional_stops: newStops })
  }

  const renderAirportSelect = (value: string, onChange: (val: string) => void, placeholder: string) => {
    const normalizedValue = AIRPORT_NORMALIZE[value] ?? value
    const isTaoyuan = normalizedValue && normalizedValue.startsWith("桃園機場")
    const airportBase = isTaoyuan ? "桃園機場" : (normalizedValue || "")
    // normalize old format "桃園機場一航廈"→"第1航廈", "桃園機場二航廈"→"第2航廈"
    const normalizeTerminal = (v: string) => {
      if (!v) return ""
      if (v === "桃園機場一航廈") return "第1航廈"
      if (v === "桃園機場二航廈") return "第2航廈"
      if (v === "桃園機場三航廈") return "第3航廈"
      return v.replace("桃園機場", "")
    }
    const terminal = isTaoyuan ? normalizeTerminal(normalizedValue) : ""
    const selectCls = "flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-2 py-1 text-sm shadow-sm outline-none cursor-pointer"
    return (
      <div className="flex gap-1">
        <select
          value={airportBase}
          onChange={e => {
            const airport = e.target.value
            onChange(airport === "桃園機場" ? (terminal ? `桃園機場${terminal}` : "桃園機場") : airport)
          }}
          className={`${selectCls} flex-1`}
          required
        >
          <option value="" disabled>{placeholder}</option>
          {AIRPORT_BASES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {isTaoyuan && (
          <select
            value={terminal}
            onChange={e => onChange(`桃園機場${e.target.value}`)}
            className={`${selectCls} w-24`}
            required
          >
            <option value="" disabled>請選擇</option>
            {TAOYUAN_TERMINALS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>
    )
  }

  // 可重用金額區塊
  const renderPricingBlock = (
    pricing: PricingDetail,
    nameboard: boolean,
    onPricingChange: (patch: Partial<PricingDetail>) => void,
    baseRequired = false,
    section: 'main' | 'return' = 'main'
  ) => {
    const focusField = (field: keyof PricingDetail, label: string) => () => setActiveFeeField({ section, field, label })
    // 填廠商回金時，司機費用自動 = 車資 - 回金一 - 回金二（之後仍可手動改司機費用）
    const rebatePatch = (rebate: number): Partial<PricingDetail> => ({
      vendor_rebate: rebate,
      driver_fee: Math.max(0, (Number(pricing.base_fare) || 0) - rebate - (Number(pricing.vendor_rebate_2) || 0)),
    })
    const rebatePatch2 = (rebate: number): Partial<PricingDetail> => ({
      vendor_rebate_2: rebate,
      driver_fee: Math.max(0, (Number(pricing.base_fare) || 0) - (Number(pricing.vendor_rebate) || 0) - rebate),
    })
    const fieldPatch = (field: keyof PricingDetail, value: number): Partial<PricingDetail> =>
      field === 'vendor_rebate' ? rebatePatch(value)
        : field === 'vendor_rebate_2' ? rebatePatch2(value)
        : ({ [field]: value } as Partial<PricingDetail>)
    const total = calcTotal(pricing, nameboard)
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <div className="space-y-1">
            <div className="h-10 flex items-center justify-center rounded-md border border-primary/20 bg-primary/10 dark:bg-primary/20 font-black text-base text-primary tabular-nums">
              {total || 0}
            </div>
            <div className="text-[10px] text-center text-primary font-bold">總金額</div>
          </div>
          <div className="space-y-1">
            <Input type="number" step={50} className="h-10 text-base border-primary/30 font-black text-center p-0"
              value={pricing.base_fare}
              onFocus={focusField('base_fare', '車資')}
              onChange={e => {
                const baseFare = parseInt(e.target.value) || 0
                const patch: Partial<PricingDetail> = { base_fare: baseFare }
                const { enabled, vendorPercent } = getFeeSplitConfig()
                if (enabled && !showVendors) {
                  const vendorRebate = Math.round(baseFare * vendorPercent / 100)
                  patch.vendor_rebate = vendorRebate
                  patch.driver_fee = baseFare - vendorRebate
                }
                onPricingChange(patch)
              }}
              required={baseRequired} />
            <button
              type="button"
              onClick={() => onPricingChange({ is_tax_inclusive: !pricing.is_tax_inclusive })}
              className={`w-full h-5 rounded text-[10px] font-bold transition-colors cursor-pointer ${pricing.is_tax_inclusive ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}
              title={pricing.is_tax_inclusive ? '此車資已含稅' : '此車資未含稅（點擊切換）'}
            >
              {pricing.is_tax_inclusive ? '含稅' : '未稅'}
            </button>
            <div className="text-[10px] text-center text-primary font-bold">車資</div>
          </div>
          <div className="space-y-1">
            <Input type="number" step={50} className="h-10 text-base text-center p-0"
              value={pricing.night_surcharge}
              onFocus={focusField('night_surcharge', '夜間')}
              onChange={e => onPricingChange({ night_surcharge: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">夜間</div>
          </div>
          <div className="space-y-1">
            <Input type="number" step={50} className="h-10 text-base text-center p-0"
              value={pricing.cross_district_fee}
              onFocus={focusField('cross_district_fee', '跨區')}
              onChange={e => onPricingChange({ cross_district_fee: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">跨區</div>
          </div>
        </div>
        {activeFeeField.section === section && (
          <div className="flex items-center justify-center flex-wrap gap-1.5">
            <span className="text-[10px] text-gray-400">金額快速加價（目前調整：{activeFeeField.label}）</span>
            {[
              { amt: 100, cls: 'bg-blue-500 hover:bg-blue-600 text-white' },
              { amt: 500, cls: 'bg-emerald-500 hover:bg-emerald-600 text-white' },
              { amt: 1000, cls: 'bg-orange-500 hover:bg-orange-600 text-white' },
            ].map(({ amt, cls }) => (
              <button key={amt} type="button"
                onClick={() => onPricingChange(fieldPatch(activeFeeField.field, (Number(pricing[activeFeeField.field]) || 0) + amt))}
                className={`h-6 px-2.5 rounded-full text-[11px] font-bold shadow-sm transition-colors cursor-pointer ${cls}`}>
                +{amt}
              </button>
            ))}
            <button type="button"
              onClick={() => onPricingChange(fieldPatch(activeFeeField.field, 0))}
              className="h-6 px-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold shadow-sm transition-colors cursor-pointer">
              歸零
            </button>
          </div>
        )}
        <div className="grid grid-cols-4 gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
          {!isHidden('tax_amount') && (
            <div className="space-y-1">
              <div className="h-10 flex items-center justify-center rounded-md border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 font-black text-base text-orange-600 dark:text-orange-400 tabular-nums">
                {pricing.is_tax_inclusive ? total : Math.round(total * 1.05)}
              </div>
              <div className="text-[10px] text-center text-orange-500 font-bold">含稅總額</div>
            </div>
          )}
          <div className="space-y-1">
            <Input type="number" step={50} className="h-10 text-base text-center p-0"
              value={pricing.driver_fee}
              onFocus={focusField('driver_fee', '司機費用')}
              onChange={e => onPricingChange({ driver_fee: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">司機費用</div>
          </div>
          {!showVendors && (
            <div className="space-y-1">
              <Input type="number" step={50} className="h-10 text-base text-center p-0"
                value={pricing.vendor_rebate}
                onFocus={focusField('vendor_rebate', '廠商回金')}
                onChange={e => onPricingChange(rebatePatch(parseInt(e.target.value) || 0))} />
              <div className="text-[10px] text-center text-gray-500 font-bold">廠商回金</div>
            </div>
          )}
          {!isHidden('company_rebate') && (
            <div className="space-y-1">
              <Input type="number" step={50} className="h-10 text-base text-center p-0"
                value={pricing.company_rebate}
                onFocus={focusField('company_rebate', '公司回金')}
                onChange={e => onPricingChange({ company_rebate: parseInt(e.target.value) || 0 })} />
              <div className="text-[10px] text-center text-gray-500 font-bold">公司回金</div>
            </div>
          )}
        </div>
        {showVendors && (
          <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
            <div className="space-y-1">
              <select
                value={formData.vendor_name || ''}
                onChange={e => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
                className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white px-2 focus:border-blue-500 focus:outline-none">
                <option value="">無廠商</option>
                {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <div className="text-[10px] text-center text-gray-500 font-bold">廠商</div>
            </div>
            <div className="space-y-1">
              <Input type="number" step={50} className="h-10 text-base text-center p-0"
                value={pricing.vendor_rebate}
                onFocus={focusField('vendor_rebate', '廠商回金')}
                onChange={e => onPricingChange(rebatePatch(parseInt(e.target.value) || 0))} />
              <div className="text-[10px] text-center text-gray-500 font-bold">廠商回金</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 h-10">
                <input
                  type="checkbox"
                  id="vendor-paid"
                  checked={!!formData.vendor_paid_at}
                  onChange={e => setFormData(prev => ({ ...prev, vendor_paid_at: e.target.checked ? new Date().toISOString().substring(0, 10) : '' }))}
                  className="h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer flex-shrink-0"
                />
                <input
                  type="date"
                  value={formData.vendor_paid_at || ''}
                  onChange={e => setFormData(prev => ({ ...prev, vendor_paid_at: e.target.value }))}
                  className="flex-1 h-8 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white px-1 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-[10px] text-center text-gray-500 font-bold">已回廠商</div>
            </div>
          </div>
        )}
        {showVendors && secondVendorEnabled && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="space-y-1">
              <select
                value={formData.vendor2_name || ''}
                onChange={e => setFormData(prev => ({ ...prev, vendor2_name: e.target.value }))}
                className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white px-2 focus:border-blue-500 focus:outline-none">
                <option value="">無第二廠商</option>
                {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <div className="text-[10px] text-center text-gray-500 font-bold">第二廠商</div>
            </div>
            <div className="space-y-1">
              <Input type="number" step={50} className="h-10 text-base text-center p-0"
                value={pricing.vendor_rebate_2 ?? 0}
                onFocus={focusField('vendor_rebate_2', '第二廠商回金')}
                onChange={e => onPricingChange(rebatePatch2(parseInt(e.target.value) || 0))} />
              <div className="text-[10px] text-center text-gray-500 font-bold">第二廠商回金</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 h-10">
                <input
                  type="checkbox"
                  id="vendor2-paid"
                  checked={!!formData.vendor2_paid_at}
                  onChange={e => setFormData(prev => ({ ...prev, vendor2_paid_at: e.target.checked ? new Date().toISOString().substring(0, 10) : '' }))}
                  className="h-4 w-4 rounded border-gray-300 accent-orange-500 cursor-pointer flex-shrink-0"
                />
                <input
                  type="date"
                  value={formData.vendor2_paid_at || ''}
                  onChange={e => setFormData(prev => ({ ...prev, vendor2_paid_at: e.target.value }))}
                  className="flex-1 h-8 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white px-1 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-[10px] text-center text-gray-500 font-bold">已回第二廠商</div>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity" />

      <div className={cn(
        "fixed right-0 top-0 h-full bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col",
        previewImageUrl ? "w-full lg:w-[1000px]" : aiRawText ? "w-full lg:w-[950px]" : "w-full md:w-2/3 lg:w-[550px]"
      )}>
        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white px-1">
            {!order ? '新增訂單' : order.id && order.id !== '' ? '編輯訂單' : '確認訂單'}
          </h2>
          <div className="flex items-center gap-1.5">
            {onSaveAsNew && order?.id && order.id !== '' && (
              <>
                <Button type="button" onClick={handleSaveAsNew} disabled={isSubmitting}
                  variant="outline"
                  className="h-8 px-3 text-xs font-bold border-primary/40 text-primary hover:bg-primary/10">
                  另存新單
                </Button>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 ml-1 mr-2" />
              </>
            )}
            <Button type="button" variant="ghost" onClick={onClose} className="h-8 px-3 text-xs text-gray-500">取消</Button>
            <Button type="submit" form="order-form" disabled={isSubmitting}
              className="h-8 px-4 text-xs font-bold bg-red-600 hover:bg-red-700 text-white">
              {isSubmitting ? '儲存中...' : !order ? '建立' : order.id && order.id !== '' ? '更新' : '確認建立'}
            </Button>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body：桌面有圖片時左右雙欄，手機有圖片時上下 */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
          {/* 圖片預覽 / AI 解析原文：手機收合式、桌機左欄固定，方便跟右側表單對照 */}
          {previewImageUrl ? (
            <ImagePreviewPanel url={previewImageUrl} />
          ) : aiRawText ? (
            <AiRawTextPanel text={aiRawText} />
          ) : null}

          {/* 表單 */}
          <div className="flex-1 overflow-y-auto overflow-x-visible">
        <form id="order-form" onSubmit={handleSubmit} className="p-4 space-y-4 scrollbar-thin"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const t = e.target as HTMLElement
              if (t.tagName === 'TEXTAREA') return
              if (t.tagName === 'BUTTON' && (t as HTMLButtonElement).type === 'submit') return
              e.preventDefault()
            }
          }}>

          {/* 類別 + 狀態 */}
          <div className="flex gap-2">
            <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-md border border-gray-200 dark:border-gray-700 flex-1">
              {categoryOptions.map(cat => (
                <Button key={cat} type="button" variant={formData.order_category === cat ? "default" : "ghost"} size="sm"
                  onClick={() => {
                    if (cat === formData.order_category) return
                    const needsAirportPickup = cat === '接機'
                    const needsAirportDropoff = cat === '送機'
                    const isAirport = (v: string) => AIRPORT_BASES.some(a => v.startsWith(a))
                    setFormData({
                      ...formData,
                      order_category: cat,
                      pickup_location: needsAirportPickup
                        ? (isAirport(formData.pickup_location) ? formData.pickup_location : '桃園機場')
                        : formData.pickup_location,
                      dropoff_location: needsAirportDropoff
                        ? (isAirport(formData.dropoff_location) ? formData.dropoff_location : '桃園機場')
                        : formData.dropoff_location,
                    })
                  }}
                  className={cn("flex-1 h-8 px-0 text-xs font-bold transition-all", formData.order_category === cat ? "shadow-sm" : "text-gray-500")}>
                  {cat}
                </Button>
              ))}
            </div>
            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
              className="w-[120px] h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-xs font-bold text-pink-600 outline-none cursor-pointer">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* 日期 / 時間：手機各佔一行，桌面並排 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 overflow-visible">
            <MonthDayPicker value={formData.pickup_date} year={formData.pickup_year}
              onYearChange={y => setFormData(prev => ({ ...prev, pickup_year: y }))}
              onChange={value => { setFormData(prev => ({ ...prev, pickup_date: value })); setFlightInfo(null); setFlightMsg(''); setTimeout(() => timePickerRef.current?.focus(), 100) }}
              placeholder="MM-DD" />
            <TimePicker ref={timePickerRef} value={formData.pickup_time}
              onChange={value => setFormData({ ...formData, pickup_time: value })} placeholder="選擇時間" />
          </div>

          {/* 航班 */}
          {!isHidden('flight_number') && (
            <div className="flex gap-1">
              <InputWithClear placeholder="航班編號" className="h-10 text-base"
                value={formData.flight_number || ''}
                onChange={e => { setFormData({ ...formData, flight_number: e.target.value }); setFlightInfo(null); setFlightMsg('') }}
                onClear={() => { setFormData({ ...formData, flight_number: undefined }); setFlightInfo(null); setFlightMsg('') }} />
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0"
                disabled={!formData.flight_number || !formData.pickup_date || flightLoading}
                onClick={handleFlightLookup}>
                <Search className={`w-4 h-4 ${flightLoading ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          )}

          {/* 航班資訊顯示 */}
          {!isHidden('flight_number') && flightInfo && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300">
              <span className="font-semibold">{flightInfo.airline} {flightInfo.flight_number}</span>
              {' | '}
              <span>{flightInfo.departure_airport} → {flightInfo.arrival_airport}</span>
              {'  '}
              <span>起飛 {flightInfo.departure_time}{flightInfo.departure_terminal ? ` 航廈 ${flightInfo.departure_terminal}` : ''}</span>
              {' · '}
              <span>抵達 {flightInfo.arrival_time}{flightInfo.arrival_terminal ? ` 航廈 ${flightInfo.arrival_terminal}` : ''}</span>
            </div>
          )}
          {!isHidden('flight_number') && flightInfo && flightInfo.connections && flightInfo.connections.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300 space-y-1">
              <div className="font-semibold">同機號接續航段（轉機）</div>
              {flightInfo.connections.map((c, idx) => (
                <div key={idx}>
                  <span>{c.departure_airport} → {c.arrival_airport}</span>
                  {'  '}
                  <span>轉機出發 {c.departure_time}{c.departure_terminal ? ` 航廈 ${c.departure_terminal}` : ''}</span>
                  {c.arrival_time && (
                    <>
                      {' · '}
                      <span>抵達 {c.arrival_time}{c.arrival_terminal ? ` 航廈 ${c.arrival_terminal}` : ''}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {!isHidden('flight_number') && flightMsg && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
              {flightMsg}
            </div>
          )}

          {/* 上下車地點 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              {formData.order_category === '接機'
                ? renderAirportSelect(formData.pickup_location, val => { setFormData({ ...formData, pickup_location: val }); setFormErrors(prev => ({ ...prev, pickup_location: '' })) }, "上車機場 *")
                : <AddressAutocomplete placeholder="上車地點 *" value={formData.pickup_location}
                    onChange={val => { setFormData({ ...formData, pickup_location: val }); setFormErrors(prev => ({ ...prev, pickup_location: '' })) }} required />}
              {formErrors.pickup_location && <p className="text-xs text-red-500 mt-0.5">{formErrors.pickup_location}</p>}
            </div>
            <div>
              {formData.order_category === '送機'
                ? renderAirportSelect(formData.dropoff_location, val => { setFormData({ ...formData, dropoff_location: val }); setFormErrors(prev => ({ ...prev, dropoff_location: '' })) }, "下車機場 *")
                : <AddressAutocomplete placeholder="下車地點 *" value={formData.dropoff_location}
                    onChange={val => { setFormData({ ...formData, dropoff_location: val }); setFormErrors(prev => ({ ...prev, dropoff_location: '' })) }} required />}
              {formErrors.dropoff_location && <p className="text-xs text-red-500 mt-0.5">{formErrors.dropoff_location}</p>}
            </div>
          </div>

          {/* 車型 + 人數/行李/座椅 */}
          <div className="flex gap-2">
            <select value={formData.vehicle_type} onChange={e => setFormData({ ...formData, vehicle_type: e.target.value })}
              className="flex h-10 flex-[1.5] rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer" required>
              <option value="">選擇車型 *</option>
              {vehicleOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <Counter value={formData.passenger_count} min={1} max={8} onChange={value => setFormData({ ...formData, passenger_count: value })} label="人數" />
            {!isHidden('luggage_count') && <Counter value={formData.luggage_count} min={0} max={8} onChange={value => setFormData({ ...formData, luggage_count: value })} label="行李" />}
            {!isHidden('child_seat') && <Counter value={formData.child_seat} min={0} max={4}
              onChange={value => setFormData(prev => ({ ...prev, child_seat: value, pricing: { ...prev.pricing, child_seat_fee: value * getTenantAddonPrices().child_seat_price } }))}
              label="兒童座椅" />}
            {!isHidden('booster_seat') && <Counter value={formData.booster_seat} min={0} max={4}
              onChange={value => setFormData(prev => ({ ...prev, booster_seat: value, pricing: { ...prev.pricing, booster_seat_fee: value * getTenantAddonPrices().booster_seat_price } }))}
              label="增高墊" />}
          </div>

          {/* 兒童座椅/增高墊費用（僅數量 > 0 時顯示） */}
          {(formData.child_seat > 0 || formData.booster_seat > 0) && (
            <div className="flex items-center gap-3 -mt-1 flex-wrap">
              {formData.child_seat > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">兒童座椅費用</span>
                  <Input type="number" step={10} className="h-8 text-sm w-24"
                    value={formData.pricing.child_seat_fee ?? 0}
                    onChange={e => setFormData(prev => ({ ...prev, pricing: { ...prev.pricing, child_seat_fee: parseInt(e.target.value) || 0 } }))} />
                </div>
              )}
              {formData.booster_seat > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">增高墊費用</span>
                  <Input type="number" step={10} className="h-8 text-sm w-24"
                    value={formData.pricing.booster_seat_fee ?? 0}
                    onChange={e => setFormData(prev => ({ ...prev, pricing: { ...prev.pricing, booster_seat_fee: parseInt(e.target.value) || 0 } }))} />
                </div>
              )}
            </div>
          )}

          {/* 客戶 */}
          {!isHidden('customer') && (
            <div className="flex gap-2 items-center">
              <CustomerCombobox customers={customers} value={formData.customer_id || ''}
                onChange={(customerId) => setFormData(prev => ({ ...prev, customer_id: customerId || undefined }))}
                className="flex-1" />
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 text-primary border-primary/40 hover:bg-primary/5" title="快速新增客戶" onClick={onRequestAddCustomer}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* 乘客姓名 / 手機 */}
          <div className={`grid gap-2 ${isHidden('passenger_phone') ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div>
              <PassengerAutocomplete placeholder="貴賓姓名 *" className="h-10 text-base" hasError={!!formErrors.passenger_name}
                value={formData.passenger_name}
                onChange={v => { setFormData(prev => ({ ...prev, passenger_name: v })); setFormErrors(prev => ({ ...prev, passenger_name: '' })) }}
                onSelect={s => setFormData(prev => ({ ...prev, passenger_name: s.passenger_name, passenger_phone: s.passenger_phone || prev.passenger_phone }))}
                required />
              {formErrors.passenger_name && <p className="text-xs text-red-500 mt-0.5">{formErrors.passenger_name}</p>}
            </div>
            {!isHidden('passenger_phone') && (
              <PassengerAutocomplete placeholder="手機號碼" className="h-10 text-base"
                value={formData.passenger_phone}
                onChange={v => setFormData({ ...formData, passenger_phone: v })}
                onSelect={s => setFormData(prev => ({ ...prev, passenger_phone: s.passenger_phone || prev.passenger_phone, passenger_name: prev.passenger_name || s.passenger_name }))} />
            )}
          </div>

          {/* 主單金額 */}
          {!isHidden('pricing') && (
            <div className="bg-primary/5 dark:bg-primary/10 p-3 rounded-md border border-primary/10">
              {renderPricingBlock(formData.pricing, formData.name_board,
                patch => setFormData({ ...formData, pricing: { ...formData.pricing, ...patch } }), true)}
            </div>
          )}

          {/* 來回 | 舉牌 | 簽單 | 客製簽單 */}
          <div className="grid grid-cols-4 gap-1 p-1 bg-gray-50 dark:bg-gray-900/50 rounded-md border border-gray-100 dark:border-gray-800">
            {!isHidden('is_round_trip') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.is_round_trip}
                  onChange={e => handleRoundTripToggle(e.target.checked)}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">來回</span>
              </label>
            ) : <div />}
            {!isHidden('name_board') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.name_board}
                  onChange={e => {
                    const checked = e.target.checked
                    const newNotes = applyNameBoardNote(formData.driver_notes, checked) || ''
                    setFormData(prev => ({
                      ...prev,
                      name_board: checked,
                      driver_notes: newNotes,
                      pricing: checked
                        ? { ...prev.pricing, name_board_fee: getTenantAddonPrices().name_board_price }
                        : prev.pricing,
                    }))
                  }}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">舉牌</span>
              </label>
            ) : <div />}
            {!isHidden('is_signed') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.is_signed}
                  onChange={e => {
                    const checked = e.target.checked
                    setFormData({ ...formData, is_signed: checked })
                    // 互斥：勾選簽單時取消客製簽單
                    if (checked) {
                      setCustomSignEnabled(false)
                      setCustomSignFile(null)
                      setCustomSignPreview(null)
                      onCustomSignFileChange?.(null)
                    }
                  }}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">簽單</span>
              </label>
            ) : <div />}
            {/* 客製簽單先隱藏；車行在設定裡開啟「迅安簽單」才會出現這個勾選框 */}
            {xunanSignEnabled ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.dispatch_template_key === 'xunan'}
                  onChange={e => setFormData({ ...formData, dispatch_template_key: e.target.checked ? 'xunan' : '' })}
                  className="w-5 h-5 text-orange-500 border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-orange-600 dark:text-orange-400">迅安簽單</span>
              </label>
            ) : <div />}
          </div>

          {/* 舉牌費用（僅勾選舉牌時顯示） */}
          {formData.name_board && (
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">舉牌費用</span>
              <Input type="number" step={10} className="h-8 text-sm w-28"
                value={formData.pricing.name_board_fee ?? 0}
                onChange={e => setFormData(prev => ({ ...prev, pricing: { ...prev.pricing, name_board_fee: parseInt(e.target.value) || 0 } }))} />
            </div>
          )}

          {/* 客製簽單上傳區 */}
          {customSignEnabled && (
            <div className="rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10 p-3 space-y-2">
              <p className="text-xs font-medium text-orange-700 dark:text-orange-400">上傳客製簽單底圖（司機簽名時會顯示在上方）</p>
              <input
                type="file"
                accept="image/*"
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200 cursor-pointer"
                onChange={e => {
                  const f = e.target.files?.[0] || null
                  setCustomSignFile(f)
                  onCustomSignFileChange?.(f)
                  if (f) {
                    const url = URL.createObjectURL(f)
                    setCustomSignPreview(url)
                  } else {
                    setCustomSignPreview(null)
                  }
                }}
              />
              {customSignPreview && (
                <img src={customSignPreview} alt="預覽" className="max-h-32 rounded border border-orange-200 object-contain" />
              )}
            </div>
          )}

          {/* 回程區塊 */}
          {formData.is_round_trip && formData.return_trip && (
            <div className="border-2 border-indigo-200 dark:border-indigo-700 rounded-xl p-4 space-y-3 bg-indigo-50/50 dark:bg-indigo-900/10">
              <div className="flex items-center justify-between">
                <span className="text-sm font-black text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  回程訂單
                </span>
                <span className="text-xs text-indigo-400">提交後自動建立（不含司機）</span>
              </div>

              {/* 回程類別 */}
              <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-md border border-gray-200 dark:border-gray-700">
                {categoryOptions.map(cat => (
                  <Button key={cat} type="button" variant={formData.return_trip!.order_category === cat ? "default" : "ghost"} size="sm"
                    onClick={() => setReturnTrip({ order_category: cat })}
                    className={cn("flex-1 h-8 px-0 text-xs font-bold transition-all", formData.return_trip!.order_category === cat ? "shadow-sm" : "text-gray-500")}>
                    {cat}
                  </Button>
                ))}
              </div>

              {/* 回程日期 / 時間 */}
              <div className="grid grid-cols-2 gap-2">
                <MonthDayPicker value={formData.return_trip.pickup_date} year={formData.return_trip.pickup_year}
                  onYearChange={y => setReturnTrip({ pickup_year: y })}
                  onChange={value => { setReturnTrip({ pickup_date: value }); setReturnFlightInfo(null); setReturnFlightMsg('') }} placeholder="MM-DD" />
                <TimePicker value={formData.return_trip.pickup_time}
                  onChange={value => setReturnTrip({ pickup_time: value })} placeholder="選擇時間" />
              </div>

              {/* 回程航班 */}
              <div className="flex gap-1">
                <InputWithClear placeholder="航班編號" className="h-10 text-base"
                  value={formData.return_trip.flight_number || ''}
                  onChange={e => { setReturnTrip({ flight_number: e.target.value }); setReturnFlightInfo(null); setReturnFlightMsg('') }}
                  onClear={() => { setReturnTrip({ flight_number: '' }); setReturnFlightInfo(null); setReturnFlightMsg('') }} />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0"
                  disabled={!formData.return_trip.flight_number || !formData.return_trip.pickup_date || returnFlightLoading}
                  onClick={handleReturnFlightLookup}>
                  <Search className={`w-4 h-4 ${returnFlightLoading ? 'animate-pulse' : ''}`} />
                </Button>
              </div>

              {/* 回程航班資訊顯示 */}
              {returnFlightInfo && (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300">
                  <span className="font-semibold">{returnFlightInfo.airline} {returnFlightInfo.flight_number}</span>
                  {' | '}
                  <span>{returnFlightInfo.departure_airport} → {returnFlightInfo.arrival_airport}</span>
                  {'  '}
                  <span>起飛 {returnFlightInfo.departure_time}{returnFlightInfo.departure_terminal ? ` 航廈 ${returnFlightInfo.departure_terminal}` : ''}</span>
                  {' · '}
                  <span>抵達 {returnFlightInfo.arrival_time}{returnFlightInfo.arrival_terminal ? ` 航廈 ${returnFlightInfo.arrival_terminal}` : ''}</span>
                </div>
              )}
              {returnFlightInfo && returnFlightInfo.connections && returnFlightInfo.connections.length > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300 space-y-1">
                  <div className="font-semibold">同機號接續航段（轉機）</div>
                  {returnFlightInfo.connections.map((c, idx) => (
                    <div key={idx}>
                      <span>{c.departure_airport} → {c.arrival_airport}</span>
                      {'  '}
                      <span>轉機出發 {c.departure_time}{c.departure_terminal ? ` 航廈 ${c.departure_terminal}` : ''}</span>
                      {c.arrival_time && (
                        <>
                          {' · '}
                          <span>抵達 {c.arrival_time}{c.arrival_terminal ? ` 航廈 ${c.arrival_terminal}` : ''}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {returnFlightMsg && (
                <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
                  {returnFlightMsg}
                </div>
              )}

              {/* 回程上下車地點 */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  {formData.return_trip.order_category === '接機'
                    ? renderAirportSelect(formData.return_trip.pickup_location, val => setReturnTrip({ pickup_location: val }), "上車機場 *")
                    : <AddressAutocomplete placeholder="上車地點 *" value={formData.return_trip.pickup_location}
                        onChange={val => setReturnTrip({ pickup_location: val })} required />}
                </div>
                <div>
                  {formData.return_trip.order_category === '送機'
                    ? renderAirportSelect(formData.return_trip.dropoff_location, val => setReturnTrip({ dropoff_location: val }), "下車機場 *")
                    : <AddressAutocomplete placeholder="下車地點 *" value={formData.return_trip.dropoff_location}
                        onChange={val => setReturnTrip({ dropoff_location: val })} required />}
                </div>
              </div>

              {/* 回程舉牌 */}
              <label className="flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={formData.return_trip.name_board}
                  onChange={e => {
                    const checked = e.target.checked
                    setFormData(prev => ({
                      ...prev,
                      return_trip: {
                        ...prev.return_trip!,
                        name_board: checked,
                        pricing: checked
                          ? { ...prev.return_trip!.pricing, name_board_fee: getTenantAddonPrices().name_board_price }
                          : prev.return_trip!.pricing,
                      },
                    }))
                  }}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">舉牌</span>
              </label>
              {formData.return_trip.name_board && (
                <div className="flex items-center gap-2 -mt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">回程舉牌費用</span>
                  <Input type="number" step={10} className="h-8 text-sm w-28"
                    value={formData.return_trip.pricing.name_board_fee ?? 0}
                    onChange={e => setReturnPricing({ name_board_fee: parseInt(e.target.value) || 0 })} />
                </div>
              )}

              {/* 回程金額 */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-md border border-indigo-100 dark:border-indigo-800">
                {renderPricingBlock(formData.return_trip.pricing, formData.return_trip.name_board, setReturnPricing, false, 'return')}
              </div>

              {returnTripError && (
                <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">{returnTripError}</div>
              )}
            </div>
          )}

          {/* 付款方式 + 司機 */}
          <div className="grid grid-cols-2 gap-2">
            <select value={formData.payment_method} onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer" required>
              <option value="">選擇付款方式 *</option>
              {paymentOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <DriverCombobox
              drivers={drivers}
              value={formData.driver_id || ''}
              onChange={handleDriverChange}
              disabledDriverIds={onLeaveDriverIds}
              unassignedLabel="未指派司機"
              triggerClassName="flex h-10 w-full items-center justify-between rounded-md border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-2 text-sm font-medium text-gray-800 dark:text-gray-100 shadow-sm outline-none cursor-pointer"
            />
          </div>

          {/* 派車單樣板（租戶有設定多個具名樣板時才顯示，沒選就用預設樣板） */}
          {dispatchTemplateOptions.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs text-gray-500 dark:text-gray-400">派車單樣板</label>
              <select value={formData.dispatch_template_key || ''}
                onChange={e => setFormData({ ...formData, dispatch_template_key: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer">
                <option value="">標準派車單（預設）</option>
                {dispatchTemplateOptions.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* 其他上車點 */}
          {!isHidden('additional_stops') && <div className="space-y-2">
            <div className="space-y-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-700 p-2">
              <div className="text-xs font-medium text-gray-500">新增第三方上車點</div>
              <div className="grid grid-cols-2 gap-2">
                <PassengerAutocomplete
                  value={newStopName}
                  onChange={setNewStopName}
                  onSelect={s => { setNewStopName(s.passenger_name || ''); setNewStopPhone(s.passenger_phone || '') }}
                  placeholder="姓名（選填）"
                  className="h-9 text-sm"
                />
                <PassengerAutocomplete
                  value={newStopPhone}
                  onChange={setNewStopPhone}
                  onSelect={s => { setNewStopPhone(s.passenger_phone || ''); if (!newStopName) setNewStopName(s.passenger_name || '') }}
                  placeholder="電話（選填）"
                  className="h-9 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <AddressAutocomplete placeholder="地址（必填）" value={newStopInput}
                    onChange={v => { setNewStopInput(v); if (newStopError) setNewStopError('') }} />
                </div>
                <Button type="button" onClick={handleAddStop} variant="outline" size="icon" className="h-10 w-10 shrink-0">
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
              {newStopError && <p className="text-red-500 text-xs">{newStopError}</p>}
            </div>
            {formData.additional_stops && formData.additional_stops.length > 0 && (
              <div className="space-y-2 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-md border border-gray-100 dark:border-gray-800">
                {formData.additional_stops.map((stop, index) => (
                  <div key={index} className="flex items-start gap-2 group">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-bold shrink-0 mt-1">{index + 1}</div>
                    <div className="flex-1 space-y-1">
                      <Input value={stop.address} onChange={e => handleEditStop(index, 'address', e.target.value)}
                        placeholder="地址"
                        className="h-8 text-sm border-transparent focus:border-input bg-transparent hover:bg-white dark:hover:bg-gray-800 transition-colors" />
                      <div className="grid grid-cols-2 gap-1">
                        <Input value={stop.name || ''} onChange={e => handleEditStop(index, 'name', e.target.value)}
                          placeholder="姓名（選填）"
                          className="h-7 text-xs border-transparent focus:border-input bg-transparent hover:bg-white dark:hover:bg-gray-800 transition-colors" />
                        <Input value={stop.phone || ''} onChange={e => handleEditStop(index, 'phone', e.target.value)}
                          placeholder="電話（選填）"
                          className="h-7 text-xs border-transparent focus:border-input bg-transparent hover:bg-white dark:hover:bg-gray-800 transition-colors" />
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveStop(index)}
                      className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>}

          {/* 客戶修改紀錄 */}
          {order?.edited_by_customer && order?.edit_history && (() => {
            let history: { edited_at: string; changes: Record<string, { label: string; before: unknown; after: unknown }> }[] = []
            try { history = JSON.parse(order.edit_history) } catch { history = [] }
            if (history.length === 0) return null
            const fmtVal = (v: unknown) => {
              if (v === null || v === undefined || v === '') return '—'
              if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v)
              if (Array.isArray(v)) {
                return v.map((s: any) => [s.name, s.phone, s.address].filter(Boolean).join(' ')).join('、') || '—'
              }
              return JSON.stringify(v)
            }
            return (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 space-y-2">
                <div className="text-xs font-semibold text-amber-700 dark:text-amber-400">客戶自行修改紀錄</div>
                {history.map((entry, i) => (
                  <div key={i} className="text-xs text-amber-800 dark:text-amber-200 space-y-0.5">
                    <div className="text-amber-500">{new Date(entry.edited_at).toLocaleString('zh-TW')}</div>
                    {Object.values(entry.changes).map((c, j) => (
                      <div key={j}>{c.label}：{fmtVal(c.before)} → {fmtVal(c.after)}</div>
                    ))}
                  </div>
                ))}
              </div>
            )
          })()}

          {/* 等待時間／追加金額（車行設定開啟才顯示） */}
          {waitExtraEnabled && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">等待時間（分鐘）</label>
                <Input type="number" min={0} inputMode="numeric" placeholder="例：30" value={formData.wait_minutes || ''}
                  onChange={e => setFormData({ ...formData, wait_minutes: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">追加金額</label>
                <Input type="number" min={0} inputMode="numeric" placeholder="例：200" value={formData.extra_amount || ''}
                  onChange={e => setFormData({ ...formData, extra_amount: e.target.value })} />
              </div>
            </div>
          )}

          {/* 備註 */}
          {!isHidden('driver_notes') && (
            <div className="space-y-2">
              <Textarea placeholder="請輸入給司機的備註資訊..." className="text-base bg-gray-50/10 min-h-[80px] resize-none"
                rows={2} value={formData.driver_notes || ''}
                onChange={e => setFormData({ ...formData, driver_notes: e.target.value })} />
              {formData.driver_notes && (
                <div className="text-xs text-gray-500 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-900/30 rounded border border-gray-200 dark:border-gray-700">
                  <div className="font-medium mb-1">預覽（URL 將變成可點擊的鏈接）：</div>
                  <div className="text-gray-700 dark:text-gray-300 break-words">
                    {(() => {
                      const urlRegex = /(https?:\/\/[^\s]+)/g
                      const parts = formData.driver_notes!.split(urlRegex)
                      return parts.map((part, index) => {
                        if (urlRegex.test(part)) {
                          return (
                            <a
                              key={index}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {part}
                            </a>
                          )
                        }
                        return part
                      })
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
          </div>{/* end right panel */}
        </div>{/* end flex body */}

        {/* 手機底部 sticky 建立按鈕 */}
        <div className="sm:hidden shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <Button type="submit" form="order-form" disabled={isSubmitting}
            className="w-full h-11 text-sm font-bold bg-red-600 hover:bg-red-700 text-white">
            {isSubmitting ? '儲存中...' : !order ? '建立派車單' : order.id && order.id !== '' ? '更新派車單' : '確認建立'}
          </Button>
        </div>

      </div>{/* end drawer */}
    </>
  )
}
